import { useEffect, useState } from 'react';
import { api } from '../api';
import * as crypto from '../crypto';
import type { ChatMessage, MediaMessage, ContactCardMessage } from '../screens/Chat';
import { t, useTranslation } from '../i18n';

interface Props {
  msg: ChatMessage;
  onAddContact: (syphraxId: string) => void;
  onReply?: (msg: ChatMessage) => void;
  onEdit?: (msg: ChatMessage) => void;
  onReact?: (msg: ChatMessage, emoji: string) => void;
  showSender?: boolean;
  senderDisplayName?: string;
  resolveDisplayName?: (syphraxId: string) => string;
  myId?: string;
  isMenuOpen: boolean;
  onToggleMenu: (id: string) => void;
}

const QUICK_REACTIONS = ['❤️', '😂', '😮', '😢', '🙏', '👍'];

function isOnlyEmoji(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed.length === 0) return false;
  const chars = Array.from(trimmed);
  if (chars.length === 0 || chars.length > 8) return false;
  return chars.every((c) => {
    const cp = c.codePointAt(0)!;
    if (cp <= 0x7f) return false;
    if (cp >= 0x2000 && cp <= 0x206f) return false;
    return true;
  });
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function MediaContent({ msg }: { msg: MediaMessage }) {
  useTranslation();
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    (async () => {
      try {
        const cipher = await api.downloadMedia(msg.mediaId);
        if (cancelled) return;
        const plain = crypto.decryptFile(cipher, msg.fileKey);
        const blob = new Blob([plain as BlobPart], { type: msg.contentType });
        createdUrl = URL.createObjectURL(blob);
        if (!cancelled) {
          setUrl(createdUrl);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [msg.mediaId, msg.fileKey, msg.contentType]);

  if (loading) return <div className="media-loading">{t('bubble.media.decrypting')}</div>;
  if (error) return <div className="media-error">{t('bubble.media.failed')}</div>;
  if (!url) return null;

  if (msg.kind === 'image') {
    return <img src={url} alt={msg.name} className="media-image" />;
  }
  if (msg.kind === 'video') {
    return <video src={url} controls className="media-video" />;
  }
  if (msg.kind === 'audio') {
    return <audio src={url} controls className="media-audio" />;
  }
  return (
    <a href={url} download={msg.name} className="media-file">
      <span className="media-file-icon">📎</span>
      <span className="media-file-meta">
        <span className="media-file-name">{msg.name}</span>
        <span className="media-file-size">{formatSize(msg.size)}</span>
      </span>
    </a>
  );
}

function ContactCard({ msg, onAdd }: { msg: ContactCardMessage; onAdd: (id: string) => void }) {
  return (
    <div className="contact-card">
      <div className="contact-card-icon">👤</div>
      <div className="contact-card-meta">
        <div className="contact-card-label">{t('bubble.contact.label')}</div>
        <div className="contact-card-id">{msg.contactSyphraxId}</div>
      </div>
      {!msg.fromMe && (
        <button className="primary contact-card-add" onClick={() => onAdd(msg.contactSyphraxId)}>
          {t('common.add')}
        </button>
      )}
    </div>
  );
}

function ReadIndicator({ msg }: { msg: ChatMessage }) {
  if (!msg.fromMe) return null;
  // v016 — 3 estados:
  //   read truthy        -> 2 aspas iluminadas (leído en algún device del peer)
  //   delivered truthy   -> 2 aspas grises    (recibido en algún device, no leído)
  //   ninguna            -> 1 aspa            (no entregado todavía)
  const read = !!msg.readAt;
  const delivered = read || !!msg.deliveredAt;
  if (read) return <span className="tick read" title={`Leído ${new Date(msg.readAt!).toLocaleString()}`}>✓✓</span>;
  if (delivered) return <span className="tick delivered" title={`Entregado ${new Date(msg.deliveredAt!).toLocaleString()}`}>✓✓</span>;
  return <span className="tick" title="Enviado">✓</span>;
}

function ReplyQuote({
  replyTo,
  resolveDisplayName,
}: {
  replyTo: NonNullable<ChatMessage['replyTo']>;
  resolveDisplayName?: (id: string) => string;
}) {
  const fromName = resolveDisplayName ? resolveDisplayName(replyTo.from) : replyTo.from;
  return (
    <div className="reply-quote">
      <div className="reply-quote-from">{fromName}</div>
      <div className="reply-quote-text">{replyTo.preview}</div>
    </div>
  );
}

export default function MessageBubble({
  msg,
  onAddContact,
  onReply,
  onEdit,
  onReact,
  showSender,
  senderDisplayName,
  resolveDisplayName,
  myId,
  isMenuOpen,
  onToggleMenu,
}: Props) {
  useTranslation();
  const isEmoji = msg.kind === 'text' && isOnlyEmoji(msg.text) && !msg.replyTo;
  const cls = `${msg.fromMe ? 'bubble me' : 'bubble them'}${isEmoji ? ' bubble-emoji' : ''}`;

  function toggleMenu(e: React.MouseEvent | React.TouchEvent | React.PointerEvent) {
    e.preventDefault();
    e.stopPropagation();
    onToggleMenu(msg.id);
  }

  function handleReply(e: React.MouseEvent | React.PointerEvent) {
    e.stopPropagation();
    onToggleMenu(msg.id);
    onReply?.(msg);
  }
  function handleEdit(e: React.MouseEvent | React.PointerEvent) {
    e.stopPropagation();
    onToggleMenu(msg.id);
    onEdit?.(msg);
  }
  function handleReact(e: React.MouseEvent | React.PointerEvent, emoji: string) {
    e.stopPropagation();
    onToggleMenu(msg.id);
    onReact?.(msg, emoji);
  }

  const canEdit = msg.fromMe && msg.kind === 'text' && !!onEdit;
  const canReply = !!onReply;

  return (
    <div className={cls} onContextMenu={toggleMenu}>
      {showSender && msg.fromSyphraxId && (
        <div className="sender">{senderDisplayName || msg.fromSyphraxId}</div>
      )}
      {msg.replyTo && <ReplyQuote replyTo={msg.replyTo} resolveDisplayName={resolveDisplayName} />}
      {msg.kind === 'text' && <div className="text">{msg.text}</div>}
      {(msg.kind === 'image' || msg.kind === 'video' || msg.kind === 'audio' || msg.kind === 'file') && (
        <MediaContent msg={msg as MediaMessage} />
      )}
      {msg.kind === 'contact' && <ContactCard msg={msg as ContactCardMessage} onAdd={onAddContact} />}
      <div className="meta">
        {msg.editedAt && <span className="edited" title={`${t('bubble.edited')} ${new Date(msg.editedAt).toLocaleString()}`}>{t('bubble.edited')}</span>}
        <span className="time">
          {new Date(msg.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
        <ReadIndicator msg={msg} />
      </div>
      {msg.reactions && Object.keys(msg.reactions).length > 0 && (
        <div className="bubble-reactions">
          {Object.entries(msg.reactions).map(([emoji, ids]) => (
            ids.length > 0 && (
              <button
                key={emoji}
                className={`reaction-pill ${myId && ids.includes(myId) ? 'mine' : ''}`}
                onPointerDown={(ev) => { ev.stopPropagation(); onReact?.(msg, emoji); }}
                title={ids.join(', ')}
              >
                <span className="reaction-emoji">{emoji}</span>
                {ids.length > 1 && <span className="reaction-count">{ids.length}</span>}
              </button>
            )
          ))}
        </div>
      )}
      {(canReply || canEdit) && (
        <button className="bubble-menu-btn" onPointerDown={toggleMenu} aria-label={t('bubble.reply')}>⋯</button>
      )}
      {isMenuOpen && (
        <div className={`bubble-menu ${msg.fromMe ? 'menu-me' : 'menu-them'}`} onClick={(e) => e.stopPropagation()}>
          {onReact && (
            <div className="bubble-reactions-quick">
              {QUICK_REACTIONS.map((e) => (
                <button key={e} onPointerDown={(ev) => handleReact(ev, e)} className="reaction-quick-btn">{e}</button>
              ))}
            </div>
          )}
          {canReply && <button onPointerDown={handleReply}>↩ {t('bubble.reply')}</button>}
          {canEdit && <button onPointerDown={handleEdit}>✎ {t('bubble.edit')}</button>}
        </div>
      )}
    </div>
  );
}
